'use client'

import { useState } from 'react'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import {
  Bell,
  BellRing,
  Check,
  ChevronRight,
  FileCheck2,
  KeyRound,
  Sparkles,
  UserPlus,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useNotificationSocket,
  type AppNotification,
  type NotificationKind,
} from '@/features/notifications'
import { formatDateTime } from '@/lib/format/date'

const KIND_ICON: Record<NotificationKind, typeof Bell> = {
  case_submitted: UserPlus,
  case_assigned: UserPlus,
  case_published: FileCheck2,
  login_details_sent: KeyRound,
  recommendation_created: Sparkles,
}

/** The backend sends no title/body — display copy is derived client-side from `kind` (+
 * `caseReference` when present). */
const KIND_COPY: Record<
  NotificationKind,
  (caseReference: string | null) => { title: string; body: string }
> = {
  case_submitted: (ref) => ({
    title: 'New case submitted',
    body: ref ? `Case ${ref} was submitted.` : 'A new case was submitted.',
  }),
  case_assigned: (ref) => ({
    title: 'Case assigned to you',
    body: ref ? `Case ${ref} is now yours.` : 'A case is now yours.',
  }),
  case_published: (ref) => ({
    title: 'Case published',
    body: ref ? `Case ${ref} is live for the investor.` : 'A case is now live.',
  }),
  login_details_sent: (ref) => ({
    title: 'Login details sent',
    body: ref ? `Credentials were emailed for case ${ref}.` : 'Login credentials were emailed.',
  }),
  // Never carries a caseReference — a recommendation is investor-level, not tied to one case.
  recommendation_created: () => ({
    title: 'New recommendation',
    body: 'Your advisor recommended a new service for you.',
  }),
}

/** One bell for all surfaces — the real endpoint auto-scopes to the caller from the auth token.
 * Rendered once in every authenticated shell (staff/admin/investor), which makes this the natural
 * place to also mount `useNotificationSocket()` — the live-connection lifetime then matches
 * exactly wherever the bell itself is mounted, with no separate wiring needed per shell.
 *
 * `clientDetailHref`, when given, makes case notifications clickable straight to that case
 * (staff/admin only — passed by `StaffShell`/`AdminShell`, reusing `routes.staff.clientDetail`/
 * `routes.admin.clientDetail`). Omitted by `InvestorShell`: investor notifications stay
 * click-to-mark-read only, since there's no investor-facing case-detail-by-id route to send them
 * to (their dashboard is scoped by flow/tab, not raw case id). Gated generically on
 * `notification.caseId` (not per-`kind`), so any future non-case kind stays read-only for free. */
export function NotificationBell({
  clientDetailHref,
}: {
  clientDetailHref?: (investorId: string) => string
} = {}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const { notifications, unreadCount } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  useNotificationSocket()

  const openCase = (n: AppNotification) => {
    if (!clientDetailHref || !n.caseId || !n.investorId) return
    router.push(`${clientDetailHref(n.investorId)}?case=${n.caseId}` as Route)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          {unreadCount > 0 ? <BellRing className="size-4.5" /> : <Bell className="size-4.5" />}
          {unreadCount > 0 && (
            <Badge className="size-4.5 absolute -right-1 -top-1 flex items-center justify-center rounded-full p-0 text-[10px] tabular-nums">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      {/* `max-h-[var(--radix-popover-content-available-height)]` + `flex flex-col overflow-hidden`
          — without this, the panel has no cap tied to the actual space Radix had to work with, so
          a long notification list can push the whole panel (header included) past the viewport
          edge instead of just scrolling internally. The header stays fixed (`shrink-0`) and the
          list (`min-h-0 flex-1`, replacing a flat `max-h-80`) fills only what's left under it, up
          to that real available height, before scrolling. */}
      <PopoverContent
        align="end"
        className="flex max-h-[var(--radix-popover-content-available-height)] w-80 flex-col overflow-hidden p-0"
      >
        <div className="flex shrink-0 items-center justify-between border-b px-3 py-2.5">
          <p className="text-sm font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => markAllRead.mutate()}
            >
              <Check className="size-3.5" /> Mark all read
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Bell />
              </EmptyMedia>
              <EmptyTitle className="text-sm">Nothing yet</EmptyTitle>
              <EmptyDescription>You&apos;re all caught up.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          // A plain scrollable div, not Radix `ScrollArea` — its `Viewport` uses `height: 100%`
          // internally, which does not reliably resolve against a `max-height`-only (no fixed
          // `height`) ancestor across browsers, so the list never actually clipped/scrolled with
          // 5+ real notifications despite the class being present. `max-height` + `overflow-y-auto`
          // on the SAME element sidesteps that entirely: fixed cap sized to ~4 rows, scrolls past
          // that, and shrinks to fit when there are fewer.
          <div className="max-h-96 overflow-y-auto">
            <ul className="flex flex-col divide-y">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  openable={!!clientDetailHref && !!n.caseId && !!n.investorId}
                  onClick={() => {
                    markRead.mutate(n.id)
                    openCase(n)
                  }}
                />
              ))}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function NotificationRow({
  notification: n,
  openable,
  onClick,
}: {
  notification: AppNotification
  /** Whether this row navigates to its case on click, in addition to marking it read. */
  openable: boolean
  onClick: () => void
}) {
  const Icon = KIND_ICON[n.kind]
  const { title, body } = KIND_COPY[n.kind](n.caseReference)
  const isUnread = n.readAt === null
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="hover:bg-muted/50 flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors"
      >
        <span className="text-foreground bg-muted mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
          <Icon className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className={isUnread ? 'text-sm font-semibold' : 'text-sm font-medium'}>
              {title}
            </span>
            {isUnread && <span className="bg-primary size-1.5 shrink-0 rounded-full" />}
          </span>
          <span className="text-muted-foreground mt-0.5 block text-xs leading-relaxed">{body}</span>
          <span className="text-muted-foreground mt-1 block text-[11px]">
            {formatDateTime(n.createdAt)}
          </span>
        </span>
        {openable && (
          <ChevronRight
            className="text-muted-foreground mt-1 size-3.5 shrink-0"
            aria-hidden="true"
          />
        )}
      </button>
    </li>
  )
}
